import { isDefined } from '@sgme/fp';
import type { CpSummaryMailRequest } from 'components/common-form/sidePanel/docToolbar/summary/models/cpSummaryMailRequest';
import { useTradeWayLabel } from 'components/secondaryMarket/shared/status/tradeWay';
import { useSPIntl } from 'hooks/useIntl';
import { useSelector } from 'react-redux';
import {
  getFormClient,
  getFormClientType,
  getFormCustodian,
  getFormQuantity,
  getFormSettlementDate,
  getFormTradeWay,
  getFormValuationDate,
} from 'state/secondaryMarket/cpOrder/cpOrderForm/cpOrderForm.selectors';
import { getCobDate, getOrderId, getTradeDate } from 'state/secondaryMarket/cpOrder/order/order.selector';
import { fromSecondaryMarket } from 'state/selectors';
import type { AppState } from 'state/state.model';
export const useCpSummary = (tabId: string) => {
  const { formatDisplayedDate, formatNumber } = useSPIntl();

  //CP Order
  const cpOrderId = useSelector((state: AppState) => getOrderId(state, tabId));
  const cpCustodian = useSelector((state: AppState) => getFormCustodian(state, tabId));
  const cpTradeWay = useSelector((state: AppState) => getFormTradeWay(state, tabId));
  const cpQuantity = useSelector((state: AppState) => getFormQuantity(state, tabId));
  const cpClient = useSelector((state: AppState) => getFormClient(state, tabId));
  const cpClientType = useSelector((state: AppState) => getFormClientType(state, tabId));
  const cpValuationDate = useSelector((state: AppState) => getFormValuationDate(state, tabId));
  const cpTradeDate = useSelector((state: AppState) => getTradeDate(state, tabId));
  const cpCobDate = useSelector((state: AppState) => getCobDate(state, tabId));
  const cpSettlementDate = useSelector((state: AppState) => getFormSettlementDate(state, tabId));
  const cpIsin = useSelector((state: AppState) =>
    fromSecondaryMarket.cpOrder.referential.product.getCpOrderBcsProductIsin(state, tabId),
  );
  const cpFileCode = useSelector((state: AppState) =>
    fromSecondaryMarket.cpOrder.referential.product.getCpOrderBcsProductFileCode(state, tabId),
  );
  const cpRic = useSelector((state: AppState) =>
    fromSecondaryMarket.cpOrder.referential.product.getCpOrderBcsProductRic(state, tabId),
  );
  const cpCashAmount = useSelector((state: AppState) =>
    fromSecondaryMarket.cpOrder.form.getFormCashAmount(state, tabId),
  );
  const cpCurrency = useSelector((state: AppState) =>
    fromSecondaryMarket.cpOrder.referential.product.getCpOrderBcsProductCurrency(state, tabId),
  );
  const cpSettlementUnit = useSelector((state: AppState) =>
    fromSecondaryMarket.cpOrder.formInput.getInputFormTradeSizeInputMode(state, tabId),
  );
  const tradeWayLabel = useTradeWayLabel({ way: cpTradeWay });
  const cpSummaryMailRequestProps: CpSummaryMailRequest = {
    cpOrderId: cpOrderId,
    cpCustodian: `${cpCustodian?.label} (${cpCustodian?.id} - ${cpCustodian?.eliotCode})`,
    cpTradeWay: tradeWayLabel,
    cpQuantity: cpQuantity,
    cpClient: cpClientType !== 'Other' ? cpClientType : `${cpClient?.label} (${cpClient?.id} - ${cpClient?.eliotCode})`,
    cpValuationDate: formatDisplayedDate(cpValuationDate),
    cpTradeDate: formatDisplayedDate(cpTradeDate),
    cpCobDate: formatDisplayedDate(cpCobDate),
    cpSettlementDate: formatDisplayedDate(cpSettlementDate),
    cpIsin: cpIsin,
    cpFileCode: cpFileCode,
    cpRic: cpRic,
    cpCashAmount: isDefined(cpCashAmount)
      ? `${formatNumber(cpCashAmount, { maximumFractionDigits: 2, minimumFractionDigits: 2 })} ${cpCurrency}`
      : ' ',
    cpCurrency: cpCurrency,
    cpSettlementUnit: isDefined(cpSettlementUnit)
      ? cpSettlementUnit.charAt(0).toUpperCase() + cpSettlementUnit.slice(1)
      : cpSettlementUnit,
  };
  return cpSummaryMailRequestProps;
};
